<template>
    <div :class="[
        `min-h-screen w-full bg-${model.modelClass} text-text pt-6 flex`,
        bodyPadding,
    ]">
        <!-- <p class="pb-6">Breadcrumbs</p> -->
        <div class="flex flex-col w-full xl:w-2/3">
            <slot v-if="$slots.imageSlot && hasImage()" name="imageSlot" />
            <ImageCarousel v-else-if="hasImage()" :mediaIds="model.mediaIds" :modelClass="modelClass"
                :useAccentBg="true" :typeId="model.type_id" :fileUrl="model.file" :bigIcon="true" />
            <div :class="[
                'flex flex-col flex-grow space-y-2',
                mainColumnPadding,
            ]">
                <div :class="[
                    hasImage() ? 'md:mt-14' : 'md:mt-2',
                    'mt-5 pb-4 xl:pb-6',
                ]">
                    <div class="flex justify-between items-center">
                        <div class="flex items-center gap-2">
                            <Tooltip v-if="
                                can.showStatistics &&
                                Object.keys(statistics).length
                            " :delay="200">
                                <template #trigger>
                                    <p :class="`px-2.5 py-1 bg-${modelClass}-accent text-${modelClass} rounded-full cursor-help whitespace-nowrap`"
                                        v-text="$lang.get(':count Views', {
                                            count: statistics.nonAdminViews,
                                        })
                                            " />
                                </template>
                                <template #content>
                                    <div :class="[
                                        'bg-tooltip ring-1 py-1 px-3 font-medium text-tooltip-text shadow w-max ring-tooltip-border-inverted rounded-lg',
                                    ]">
                                        <table>
                                            <tbody>
                                                <tr v-for="row in statisticsPopupRows">
                                                    <td v-if="row == null" colspan="2">
                                                        <div class="w-full my-2 h-px bg-border" />
                                                    </td>
                                                    <template v-else>
                                                        <td class="pr-2" v-text="row.label" />
                                                        <td v-text="row.value" />
                                                    </template>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </template>
                            </Tooltip>
                            <span v-else
                                :class="`w-fit font-bold text-lg font-header capitalize text-${modelClass}-accent rounded-attribute tracking-wide`"
                                v-text="$getBaseModelTranslation({
                                    modelName: modelClass,
                                })
                                    " />
                            <div v-if="
                                ['draft', 'archived'].includes(
                                    model.status_id
                                ) && model?.status?.name
                            " class="px-2.5 py-1 rounded-full border border-gray-300 bg-gray-50 flex items-center"
                                v-text="model?.status?.name" />
                        </div>
                        <div class="flex items-center md:gap-3">
                            <div class="hidden md:block">
                                <slot name="toolRowSlot" />
                            </div>
                            <button v-if="!!$page.props.auth.user" @click="onToggleFavorite(model)" :class="[
                                isFavorite
                                    ? `border border-${modelClass}-accent text-${modelClass} bg-${modelClass}-accent`
                                    : `border border-transparent hover:border-${modelClass}-accent text-${modelClass}-accent`,
                                `flex gap-2 items-center pb-1 md:py-1.5 md:px-3 group rounded-button transition duration-button-hover`,
                            ]">
                                <div :class="[
                                    isFavorite
                                        ? `text-${modelClass}`
                                        : `text-${modelClass}-accent`,
                                    `relative w-10 h-10`,
                                ]">
                                    <PhBookmarkSimple class="w-full h-full" />
                                    <PhBookmarkSimple
                                        class="opacity-0 group-hover:opacity-100 transition duration-button-hover absolute left-0 top-0 w-full h-full"
                                        weight="fill" />
                                </div>
                                <p class="hidden md:block" v-text="$lang.get(
                                    isFavorite
                                        ? $lang.get(
                                            'Remove from favorites'
                                        )
                                        : $lang.get('Save favorite')
                                )
                                    " />
                            </button>
                            <ButtonPopoverMenu :items="menuItems">
                                <template #button="{ open }">
                                    <div :class="[
                                        open
                                            ? `border-${modelClass}-accent bg-${modelClass}-accent text-${modelClass}`
                                            : 'border-transparent',
                                        `border hover:border-${modelClass}-accent h-14 w-14 p-2 rounded-full transition duration-button-hover focus-style`,
                                    ]">
                                        <PhDotsThreeOutline :class="['flex-grow']" weight="fill" />
                                    </div>
                                </template>
                            </ButtonPopoverMenu>
                        </div>
                    </div>
                    <div class="md:hidden" v-if="$slots.toolRowSlot">
                        <slot name="toolRowSlot" />
                    </div>

                    <h1 :class="`mt-8 text-${modelClass}-accent hyphens-auto sm:hyphens-none`" v-text="model.name" />
                    <div class="mt-5 flex items-center gap-x-3 text-xs">
                        <p class="py-[5px]" v-text="$moment(model.created_at).format('L')" />
                        <div class="flex items-center gap-x-2">
                            <TypeTag v-if="model.type" :type="model.type" class="border-gray-300 bg-gray-50" />
                            <slot name="tagSlot" />
                            <AccessTag v-if="model.access" :access="model.access" />
                        </div>
                    </div>

                    <div v-if="$slots.LinkSlot">
                        <slot name="LinkSlot" />
                    </div>

                    <div v-if="owners.length" class="flex gap-x-1.5">
                        <div v-for="(owner, index) in owners" class="flex items-end">
                            <p v-if="
                                index == owners.length - 1 &&
                                owners.length > 1
                            " v-text="'&'" class="mr-1.5" />
                            <OwnerLink :key="owner.id" :owner="owner" class="mt-4" :title="ownersTitle"
                                :showTitle="index == 0" />
                            <p v-if="
                                owners.length > 1 &&
                                index < owners.length - 2
                            " v-text="','" class="mr-1" />
                        </div>
                    </div>
                    <OwnerLink v-else-if="model.user" :owner="model.user" class="mt-4"
                        :title="$lang.get('Created by')" />

                    <OwnerLink v-if="otherOwner" :owner="otherOwner" class="mt-4" :title="otherOwnerTitle" />

                    <div v-if="model.url" class="flex items-center gap-x-1.5 mt-1">
                        <p class="flex-shrink-0" v-text="(urlLabel || $lang.get('Read more at')) + ':'
                            " />

                        <DynamicLink :href="model.url" class="underline-hover text-link truncate focus-style"
                            v-text="model.url" />
                    </div>
                </div>

                <div v-if="
                    !showRolePanelsToTheRight && populatedRolePanels.length
                " class="flex flex-col pb-8">
                    <RolePanel v-for="panel in populatedRolePanels" :key="panel.title" :name="panel.title"
                        :roles="panel.roles" :collapsable="true" />
                </div>

                <div v-if="$slots.AfterRoleSlot && !showRightSidebar" class="pb-8">
                    <slot name="AfterRoleSlot" :collapsable="true" />
                </div>

                <div v-if="$slots.FirstBodySlot" class="my-2 pb-14">
                    <slot name="FirstBodySlot" />
                </div>

                <DescriptionTabs v-if="descriptionTabs.length" :tabs="descriptionTabs"
                    :classes="$slots.SecondBodySlot ? 'pb-6' : 'pb-14'" />

                <div v-if="$slots.SecondBodySlot" class="my-2 pb-14">
                    <slot name="SecondBodySlot" />
                </div>

                <!-- Bottom part of main column, if smaller than xl we have another color shade -->
                <div :class="[
                    'pt-6',
                    showResourcesToTheRight ? '!mt-0' : '!mt-16',
                    showAlternateColorAtBottom
                        ? `bg-${modelClass}-light xl:bg-transparent`
                        : '',
                    negativeMainColumnPadding,
                    bodyBottomPadding,
                    'flex-grow',
                ]">
                    <div :class="mainColumnPadding">
                        <div :class="[bodyPadding]">
                            <!-- Add resource to the right sidebar button, show if resources are added, otherwise a button will be show in the top right corner -->
                            <AddResourceButton v-if="
                                can.addChildren && !showResourcesToTheRight
                            " :modelId="model.id" :modelClass="modelClass" :useTopMargin="true" />

                            <ResourcePanel v-if="!showResourcesToTheRight" v-for="group in rightSidebarResources"
                                :key="group.title" :title="group.title" :resources="group.resources"
                                :resourceableModelClass="modelClass" />

                            <RelationPanel v-if="filteredRelations.length" :relations="filteredRelations"
                                :modelClass="modelClass" :canAddRelations="can.addChildren"
                                :availableEmotions="availableEmotions" />

                            <component v-if="!showAffiliationPanelsToTheRight" v-for="panel in affiliationPanels"
                                @reloadRelations="
                                    $emit('reloadAffiliationPanel', panel.id)
                                    " :is="panel.component" :id="panel.id" :parentModel="panel.parentModel"
                                :sentRequests="panel.sentRequests" :recievedRequests="panel.recievedRequests"
                                :items="panel.items || []" :canEdit="can.update" :openOnLoad="panel.openOnLoad"
                                :activeTab="panel.activeTab" class="mt-8" />

                            <ModelInteractions v-if="!interactionsDisabled && !strippedView" :modelId="model.id"
                                :modelClass="model.modelClass" :availableEmotions="availableEmotions"
                                :modelEmotions="model.emotions" :interactivityId="model.interactivity_id"
                                :initialCommentCount="model.comments_count" />

                            <Attributes class="pt-14 md:pt-24" v-if="model.groupedAttributes.length" :title="modelClass == 'user'
                                ? $page.props?.auth?.user?.id ==
                                    model.id
                                    ? $lang.get('My interests')
                                    : $lang.get('Interests')
                                : $lang.get('Attributes')
                                " :attributeGroups="model.groupedAttributes" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="showRightSidebar" :class="[
            'w-[calc(33%-theme(spacing.8))] ml-8 pl-8 border-l border-border min-h-full',
            bodyBottomPadding,
        ]">
            <div class="space-y-16">
                <!-- Add resource to the right sidebar button, only shown if no resources are added yet -->

                <AddResourceButton v-if="
                    can.addChildren &&
                    showRightSidebar &&
                    rightSidebarResources.length == 0
                " :modelId="model.id" :modelClass="modelClass" />

                <RolePanel v-if="showRolePanelsToTheRight && contactRolePanel" :key="contactRolePanel.title"
                    :name="contactRolePanel.title" :roles="contactRolePanel.roles" />

                <slot v-if="$slots.AfterRoleSlot && showRightSidebar" :collapsable="false" name="AfterRoleSlot" />

                <div>
                    <div class="flex justify-end mt-6 -mb-3 -mr-2 md:-mr-5"
                        v-if="can.addChildren && showResourcesToTheRight">
                        <ButtonPopoverMenu :items="sidebarMenuItems">
                            <template #button="{ open }">
                                <div :class="[
                                    open
                                        ? `border-${modelClass}-accent bg-${modelClass}-accent text-${modelClass}`
                                        : `border-transparent bg-${modelClass} text-${modelClass}-accent`,
                                    `hover:border-${modelClass}-accent
                          rounded-full transition duration-button-hover focus-style p-1.5`,
                                ]">
                                    <PhPlus :class="['flex-grow size-10']" weight="bold" />
                                </div>
                            </template>
                        </ButtonPopoverMenu>
                    </div>

                    <!-- Resources with display_id secondarydisplay -->
                    <DraggableGroupPanel v-if="showDraggableGroups" v-model="this.model.formattedRightColumnResources"
                        :model="modelClass" @cancelEditSortGroups="editDraggableGroups" />

                    <DraggableResourcePanel v-if="showDraggableResources"
                        v-model="this.model.formattedRightColumnResources" :modelClass="modelClass"
                        @cancelEditSortResources="editDraggableResources" />

                    <ResourcePanel v-if="showResourcesToTheRight && !showDraggableGroups && !showDraggableResources"
                        v-for="group in rightSidebarResources" :key="group.title" :title="group.title" :id="group.id"
                        :resources="group.resources" :resourceableModelClass="modelClass" :resourceable="model"
                        :isToTheRight="true" :can="can" />
                </div>

                <component v-if="
                    showAffiliationPanelsToTheRight &&
                    appliedBreakpoints.includes('xl')
                " v-for="panel in affiliationPanels" @reloadRelations="$emit('reloadAffiliationPanel', panel.id)"
                    class="" :is="panel.component" :id="panel.id" :parentModel="panel.parentModel"
                    :sentRequests="panel.sentRequests" :recievedRequests="panel.recievedRequests"
                    :items="panel.items || []" :canEdit="can.update" :openOnLoad="panel.openOnLoad"
                    :activeTab="panel.activeTab" :isInQueue="isInQueue" />

                <!-- The remaining role panels  except contact -->
                <RolePanel v-if="showRolePanelsToTheRight" v-for="panel in otherRolePanels" :key="panel.title"
                    :name="panel.title" :roles="panel.roles" />
            </div>

            <SidebarRelationPanel v-if="filteredSidebarRelations.length" :sidebarRelations="filteredSidebarRelations"
                :modelClass="modelClass" :canAddRelations="can.addChildren" :availableEmotions="availableEmotions" />

            <RightColumnGroupModal :show="showModal" @close="showModal = false" :model="model"
                :modelClass="modelClass" />

        </div>
    </div>
    <ConfirmDeleteBaseModelModal ref="confirmDeleteModal" :modelClass="modelClass" :modelId="model.id" />
    <ConfirmArchiveBaseModelModal v-if="can?.archive && archivableModel" ref="confirmArchiveModal"
        :modelClass="modelClass" :modelId="model.id" />
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue'
import OwnerLink from '@/Components/ModelComponents/OwnerLink.vue'
import DescriptionTabs from '@/Components/ModelComponents/DescriptionTabs.vue'
import { Link, router } from '@inertiajs/vue3'
import DynamicLink from '@/Components/Utilities/DynamicLink.vue'
import ModelInteractions from '@/Components/ModelComponents/Interactions/ModelInteractions.vue'
import Attributes from '@/Components/ModelComponents/Attributes.vue'
import ButtonPopoverMenu from '@/Components/Utilities/ButtonPopoverMenu.vue'
import Tooltip from '@/Components/Elements/Tooltip.vue'
import ConfirmDeleteBaseModelModal from '@/Components/Modals/ConfirmDeleteBaseModelModal.vue'
import ConfirmArchiveBaseModelModal from '@/Components/Modals/ConfirmArchiveBaseModelModal.vue'
import RolePanel from '@/Components/ModelComponents/RolePanel.vue'
import ResourcePanel from '@/Components/Panels/ResourcePanel/ResourcePanel.vue'
import AddResourceButton from '@/Components/Panels/ResourcePanel/AddResourceButton.vue'
import RelationPanel from '@/Components/Panels/RelationPanel/RelationPanel.vue'
import TypeTag from '@/Components/Elements/Tags/TypeTag.vue'
import AccessTag from '@/Components/Elements/Tags/AccessTag.vue'
import {
    PhBookmarkSimple,
    PhDotsThreeOutline,
    PhEnvelope,
    PhPlus,
} from '@phosphor-icons/vue'
import RightColumnGroupModal from '@/Components/Modals/RightColumnGroupModal.vue'
// pinia
import DraggableGroupPanel from '@/Components/Panels/ResourcePanel/DraggableGroupPanel.vue'
import DraggableResourcePanel from '@/Components/Panels/ResourcePanel/DraggableResourcePanel.vue'
import { mapActions, mapState } from 'pinia'
import { shortcutStore } from '@/Store/shortcuts'
import { screenStore } from '@/Store/screen'
import SidebarRelationPanel from '@/Components/Panels/RelationPanel/SidebarRelationPanel.vue'

import { VueDraggable } from 'vue-draggable-plus'

export default {
    components: {
        // Components
        ImageCarousel: defineAsyncComponent(() =>
            import('@/Components/Elements/ImageCarousel.vue')
        ),
        OwnerLink,
        DescriptionTabs,
        Link,
        DynamicLink,
        ModelInteractions,
        Attributes,
        ButtonPopoverMenu,
        ConfirmDeleteBaseModelModal,
        ConfirmArchiveBaseModelModal,
        RolePanel,
        ResourcePanel,
        AddResourceButton,
        RelationPanel,
        Tooltip,
        TypeTag,
        AccessTag,
        // Icons
        PhBookmarkSimple,
        PhDotsThreeOutline,
        PhEnvelope,
        PhPlus,
        SidebarRelationPanel,
        RightColumnGroupModal,
        VueDraggable,
        DraggableGroupPanel,
        DraggableResourcePanel
    },

    emits: ['reloadAffiliationPanel', 'reloadResourcePanel'],

    props: {
        model: {
            type: Object,
            required: true,
        },
        owners: {
            type: Array,
            required: true,
        },
        ownersTitle: {
            type: [String, null],
            default: null,
        },
        otherOwner: {
            type: [Object, null],
            required: false,
        },
        otherOwnerTitle: {
            type: [String, null],
            default: null,
        },
        access: {
            type: [Object, null],
        },
        can: {
            type: Object,
            required: true,
            validator: (value) => {
                return (
                    value.hasOwnProperty('update') &&
                    value.hasOwnProperty('delete') &&
                    value.hasOwnProperty('addChildren') &&
                    value.hasOwnProperty('message') &&
                    value.hasOwnProperty('archive') &&
                    value.hasOwnProperty('showStatistics')
                )
            },
        },
        // If true, the view will be very stripped, used for example in organizations where the user does not hve access but should be able to request to join
        strippedView: {
            type: Boolean,
            default: false,
        },
        relations: {
            type: Array,
            default: () => [],
        },
        sidebarRelations: {
            type: Array,
            default: () => [],
        },
        interactionsDisabled: {
            type: Boolean,
            default: false,
        },
        descriptionTabs: {
            type: Array,
            required: true,
        },
        rolePanels: {
            type: Array,
            default: () => [],
        },
        availableEmotions: {
            type: Array,
            default: () => [],
        },
        urlLabel: {
            type: String,
            default: null,
        },
        statistics: {
            type: Object,
            default: {},
        },
        isInQueue: {
            type: Boolean,
            default: false,
        },
        affiliationPanels: {
            type: Array,
            default: [],
            validator: (value) => {
                return value.every((panel) =>
                    Object.keys(panel).includes(
                        'component',
                        'items',
                        'sentRequests',
                        'recievedRequests'
                    )
                )
            },
        },
    },

    data: () => ({
        menuItems: [],
        sidebarMenuItems: [],
        showModal: false,
        showDraggableGroups: false,
        showDraggableResources: false
    }),

    created() {
        this.bodyPadding = 'px-4 md:px-14'
        this.negativeMainColumnPadding = '-mx-4 md:-mx-20'
        this.mainColumnPadding = 'px-0 md:px-6'
        this.bodyBottomPadding = 'pb-4 md:pb-14'

        this.modelClass = this.model.modelClass
        this.addMenuItems()
        this.addSidebarMenuItems()
    },

    computed: {
        ...mapState(shortcutStore, ['favorites']),
        ...mapState(screenStore, [
            'remainingBreakpoints',
            'appliedBreakpoints',
        ]),

        userId() {
            return this.$page.props.auth?.user?.id
        },

        isFavorite() {
            return this.favorites.some(
                (favorite) =>
                    favorite.id === this.model.id &&
                    favorite.modelClass === this.modelClass
            )
        },



        /**
         * Populate the roles in the role panels by filtering the accessible item roles
         * by the panels role_ids
         */
        populatedRolePanels() {
            if (this.model?.accessible_item_roles?.length) {
                return this.rolePanels
                    .map((panel) => {
                        return {
                            ...panel,
                            roles: this.model.accessible_item_roles.filter(
                                (role) => panel.role_id == role.role_id
                            ),
                        }
                    })
                    .filter((panel) => panel.roles.length)
            }
            return []
        },

        /**
         * Get the contact role panel
         */
        contactRolePanel() {
            return this.populatedRolePanels.find(
                (panel) => panel.role_id == 'contact'
            )
        },

        /**
         * Get all role panels except the contact role panel
         */
        otherRolePanels() {
            return this.populatedRolePanels.filter(
                (panel) => panel.role_id != 'contact'
            )
        },

        rightSidebarResources() {
            return this.model?.formattedRightColumnResources ?? []
        },

        showResourcesToTheRight() {
            return (
                this.rightSidebarResources.length &&
                !this.strippedView &&
                this.appliedBreakpoints.includes('xl')
            )
        },

        showRolePanelsToTheRight() {
            return (
                this.populatedRolePanels.length &&
                !this.strippedView &&
                this.appliedBreakpoints.includes('xl')
            )
        },

        showAffiliationPanelsToTheRight() {
            return (
                this.affiliationPanels.length > 0 &&
                !this.strippedView &&
                this.appliedBreakpoints.includes('xl')
            )
        },

        showRightSidebar() {
            return (
                (this.showResourcesToTheRight ||
                    this.showRolePanelsToTheRight ||
                    this.showAffiliationPanelsToTheRight ||
                    !!this.$slots.AfterRoleSlot) &&
                !this.strippedView &&
                this.appliedBreakpoints.includes('xl')
            )
        },

        /**
         * Filters the relations to only show the ones that have data
         * Or show them even if empty if the user has the permission to add relations
         */
        filteredRelations() {
            return this.relations.filter((relation) => {
                return (
                    relation.pagination?.data?.length > 0 ||
                    this.can.addChildren
                )
            })
        },

        filteredSidebarRelations() {
            return this.sidebarRelations.filter((relation) => {
                return (
                    relation.pagination?.data?.length > 0 &&
                    relation.modelClass === 'organization'
                )
            })
        },

        showAlternateColorAtBottom() {
            let showResourcePanelinMainColumn =
                !this.showResourcesToTheRight &&
                this.rightSidebarResources.length
            return (
                !this.appliedBreakpoints.includes('xl') &&
                (showResourcePanelinMainColumn ||
                    this.model.groupedAttributes.length ||
                    this.filteredRelations.length)
            )
        },

        statisticsPopupRows() {
            return [
                {
                    label: this.$lang.get('All views'),
                    value: this.statistics.totalViews,
                },
                {
                    label: this.$lang.get('Views by admins'),
                    value: this.statistics.adminViews,
                },
                null,
                {
                    label: this.$lang.get('Unique users'),
                    value: this.statistics.uniqueUsers,
                },
                {
                    label: this.$lang.get('Unique guests'),
                    value: this.statistics.uniqueGuests,
                },
                {
                    label: this.$lang.get('Unique admins'),
                    value: this.statistics.uniqueAdmins,
                },
            ]
        },

        archivableModel() {
            return [
                'usecase',
                'organization',
                'resource',
                'product',
                'learning',
                'event',
            ].includes(this.modelClass)
        },


    },

    methods: {
        ...mapActions(shortcutStore, ['addFavorite', 'deleteFavorite']),

        hasImage() {
            return this.model.mediaIds.length > 0
        },

        handleUpdatedGroups(value) {
            this.rightSidebarResources = value
        },

        addSidebarMenuItems() {
            this.sidebarMenuItems = []

            if (this.can.addChildren) {
                this.sidebarMenuItems.push({
                    text: this.$lang.get('Add :model', {
                        model: this.$getBaseModelTranslation({
                            modelName: 'resource',
                        }),

                    }),
                    href: this.$localizedRoute('resource.create', {
                        resourceable_id: this.model.id,
                        resourceable_type: this.modelClass,
                        display_id: 'secondarydisplay'
                    }),
                    type: 'link',
                })

                this.sidebarMenuItems.push({
                    text: this.$lang.get('Add title'),
                    type: 'button',
                    action: () => { this.showRightColumnGrupModal() }
                })

                this.sidebarMenuItems.push({
                    text: this.$lang.get('Sort titles'),
                    type: 'button',
                    action: () => {
                        this.editDraggableGroups()
                    }
                })

                this.sidebarMenuItems.push({
                    text: this.$lang.get('Sort resources'),
                    type: 'button',
                    action: () => {
                        this.editDraggableResources()
                    }
                })

            }
        },

        addMenuItems() {
            this.menuItems = []
            let adminItems = []
            if (this.can.update) {
                adminItems.push({
                    text: this.$lang.get('Edit'),
                    href: this.$localizedRoute(
                        `${this.modelClass}.edit`,
                        this.model.id
                    ),
                    type: 'link',
                })
            }
            if (this.can.delete) {
                adminItems.push({
                    text: this.$lang.get('Delete'),
                    type: 'button',
                    action: () => {
                        this.deleteModel()
                    },
                })
            }
            if (this.can.archive && this.archivableModel) {
                adminItems.push({
                    disabled: this.model.status_id == 'archived' ?? false,
                    text: this.$lang.get('Archive'),
                    type: 'button',
                    action: () => {
                        this.archiveModel()
                    },
                })
            }

            let otherItems = []
            if (
                ['user', 'organization'].includes(this.modelClass) &&
                this.can.message &&
                this.userId &&
                this.model.id != this.userId
            ) {
                otherItems.push({
                    icon: markRaw(PhEnvelope),
                    text: this.$lang.get('Send message'),
                    type: 'button',
                    action: () => {
                        router.post(
                            route('chats.store', {
                                participant_id: this.userId,
                                participant_type: 'user',
                                participants: [
                                    {
                                        id: this.model.id,
                                        type: this.model.modelClass,
                                    },
                                    {
                                        id: this.userId,
                                        type: 'user',
                                    },
                                ],
                            })
                        )
                    },
                })
            }

            this.menuItems.push(...otherItems)
            if (adminItems.length && otherItems.length) {
                this.menuItems.push({
                    type: 'divider',
                })
            }
            this.menuItems.push(...adminItems)
        },
        showRightColumnGrupModal() {
            this.showModal = !this.showModal
        },
        editDraggableGroups() {
            this.showDraggableResources = false
            this.showDraggableGroups = !this.showDraggableGroups
        },
        editDraggableResources() {
            this.showDraggableGroups = false
            this.showDraggableResources = !this.showDraggableResources
        },
        deleteModel() {
            this.$refs.confirmDeleteModal.confirmDelete()
        },

        archiveModel() {
            this.$refs.confirmArchiveModal.confirmArchive()
        },

        onToggleFavorite(model) {
            if (this.isFavorite) {
                this.deleteFavorite(model)
            } else {
                this.addFavorite(model)
            }
        },
    },
}
</script>
